import { useMemo } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { getDefaultStyles, sectionStyles } from 'components/shared/form/Select/customStyles';
import Group from 'models/Group';
import { IndexParams, infiniteGroupsQuery } from 'services/api/group';

const SECTION_LABELS: Record<string, string> = {
  preferred: 'Preferred providers',
  additional: 'Additional providers',
};

type LoadedGroups = { data?: { pages: { data: Group[] }[] } };

const preferredIdsIn = (data: LoadedGroups['data']) =>
  data?.pages.flatMap((page) => page.data.filter((group) => group.preferred).map((group) => group.id)) ?? [];

// The API returns preferred providers first, so grouping in arrival order gives the two sections. Whether
// there are any comes from the unsearched list (which these pickers load as soon as the provider type is
// set), so a search keeps the legend and the sections, even if it only matches one of them. A list
// without any looks the way it always has.
//
// isPreferred reads the loaded results, not the option itself: a provider picked before "Managed By"
// changed still carries the old location's flag.
export default function usePreferredSections(options: LoadedGroups, params: IndexParams) {
  // The picker's own query is keyed by its last search, even an emptied one, and keeps that search when
  // the picking location changes, so this loads the unsearched list itself rather than only reading it.
  const unsearched = useInfiniteQuery({ ...infiniteGroupsQuery(params), enabled: !!params.preferredForGroupId });

  const unsearchedIds = useMemo(() => preferredIdsIn(unsearched.data), [unsearched.data]);
  const preferredIds = useMemo(
    () => new Set([...unsearchedIds, ...preferredIdsIn(options.data)]),
    [unsearchedIds, options.data]
  );
  const hasPreferred = unsearchedIds.length > 0;

  return {
    hasPreferred,
    isPreferred: (group: Group) => preferredIds.has(group.id),
    sectionProps: hasPreferred
      ? {
          groupBy: (group: Group) => (group.preferred ? 'preferred' : 'additional'),
          getGroupOptionLabel: (key: string) => SECTION_LABELS[key] ?? key,
          styles: { ...getDefaultStyles(), ...sectionStyles },
        }
      : {},
  };
}
